import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Switch, message, Collapse, Space, Divider, Tooltip, Tag, Empty } from 'antd';
import { FacebookOutlined, InfoCircleOutlined, SaveOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
// Import de l'API centralisée pour Facebook Pixel Categories
import { facebookPixelCategoriesApi } from '../../utils/api';
// Import des catégories depuis le fichier centralisé
import { categories as productCategories } from '../../constants/productCategories';

const { Panel } = Collapse;

/**
 * Composant pour gérer les configurations Facebook Pixel par catégorie
 * Permet de configurer un pixel différent pour chaque catégorie de produit
 */
const FacebookPixelCategories = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [categoryConfigs, setCategoryConfigs] = useState({});

  // Utiliser useRef pour stocker les références de formulaires sans causer de re-render
  const formsRef = React.useRef({});

  // Liste des catégories disponibles (importée depuis constants/productCategories.js)
  const availableCategories = productCategories;

  useEffect(() => {
    loadCategoryConfigs();
  }, []);

  /**
   * Charge toutes les configurations de pixels par catégorie
   */
  const loadCategoryConfigs = async () => {
    try {
      setLoading(true);

      // Récupérer les configurations existantes via l'API centralisée
      const response = await facebookPixelCategoriesApi.getAll();

      if (response.data.success) {
        // Transformer en objet avec category_name comme clé
        const configsMap = {};
        response.data.data.forEach(config => {
          configsMap[config.category_name] = config;
        });
        setCategoryConfigs(configsMap);
      }

    } catch (error) {
      console.error('Erreur lors du chargement des configurations:', error);
      message.error('Erreur lors du chargement des configurations par catégorie');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sauvegarde ou met à jour la configuration d'une catégorie
   * @param {string} categoryName - Nom de la catégorie
   * @param {Object} values - Valeurs du formulaire
   */
  const saveCategoryConfig = async (categoryName, values) => {
    try {
      setSaving({ ...saving, [categoryName]: true });

      // Validation
      if (!values.facebook_pixel_id || !values.facebook_access_token) {
        message.warning('Le Pixel ID et le token d\'accès sont requis');
        return;
      }

      // Préparer les données
      const configData = {
        category_name: categoryName,
        facebook_pixel_id: values.facebook_pixel_id.trim(),
        facebook_access_token: values.facebook_access_token.trim(),
        facebook_test_code: values.facebook_test_code?.trim() || null,
        facebook_enabled: values.facebook_enabled !== undefined ? values.facebook_enabled : true
      };

      // Envoyer la requête via l'API centralisée
      const response = await facebookPixelCategoriesApi.create(configData);

      if (response.data.success) {
        message.success(`Configuration sauvegardée pour "${categoryName}"`);

        // Mettre à jour l'état local
        setCategoryConfigs({
          ...categoryConfigs,
          [categoryName]: response.data.data
        });
      }

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error(`Erreur lors de la sauvegarde de la configuration pour "${categoryName}"`);
    } finally {
      setSaving({ ...saving, [categoryName]: false });
    }
  };

  /**
   * Supprime la configuration d'une catégorie (revient à la config globale)
   * @param {string} categoryName - Nom de la catégorie
   */
  const deleteCategoryConfig = async (categoryName) => {
    try {
      setDeleting({ ...deleting, [categoryName]: true });

      // Supprimer via l'API centralisée
      const response = await facebookPixelCategoriesApi.delete(categoryName);

      if (response.data.success) {
        message.success(`Configuration supprimée pour "${categoryName}". La configuration globale sera utilisée.`);

        // Retirer de l'état local
        const newConfigs = { ...categoryConfigs };
        delete newConfigs[categoryName];
        setCategoryConfigs(newConfigs);

        // Réinitialiser le formulaire
        if (formsRef.current[categoryName]) {
          formsRef.current[categoryName].resetFields();
        }
      }

    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      message.error(`Erreur lors de la suppression de la configuration pour "${categoryName}"`);
    } finally {
      setDeleting({ ...deleting, [categoryName]: false });
    }
  };

  /**
   * Active/désactive la configuration d'une catégorie
   * @param {string} categoryName - Nom de la catégorie
   * @param {boolean} enabled - Statut d'activation
   */
  const toggleCategoryConfig = async (categoryName, enabled) => {
    try {
      // Toggle via l'API centralisée
      const response = await facebookPixelCategoriesApi.toggle(categoryName, enabled);

      if (response.data.success) {
        message.success(`Configuration ${enabled ? 'activée' : 'désactivée'} pour "${categoryName}"`);

        // Mettre à jour l'état local
        setCategoryConfigs({
          ...categoryConfigs,
          [categoryName]: {
            ...categoryConfigs[categoryName],
            facebook_enabled: enabled
          }
        });
      }

    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      message.error('Erreur lors du changement de statut');
    }
  };

  /**
   * Génère le header du panel pour chaque catégorie
   * @param {string} categoryName - Nom de la catégorie
   */
  const getPanelHeader = (categoryName) => {
    const config = categoryConfigs[categoryName];
    const hasConfig = !!config;
    const isEnabled = hasConfig && config.facebook_enabled;

    return (
      <div className="flex items-center justify-between w-full pr-4">
        <Space>
          <FacebookOutlined className="text-blue-600" data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} />
          <span className="font-medium">{categoryName}</span>
          {hasConfig && (
            <Tag color={isEnabled ? 'green' : 'orange'}>
              {isEnabled ? 'Configuré et actif' : 'Configuré mais désactivé'}
            </Tag>
          )}
          {!hasConfig && (
            <Tag color="default">Configuration globale utilisée</Tag>
          )}
        </Space>
      </div>
    );
  };

  /**
   * Génère le contenu du panel pour chaque catégorie
   * @param {string} categoryName - Nom de la catégorie
   */
  const getPanelContent = (categoryName) => {
    const config = categoryConfigs[categoryName];

    return (
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <InfoCircleOutlined className="text-blue-600 mt-1" />
            <div className="text-sm text-blue-800">
              <p>
                Configurez un pixel Facebook spécifique pour la catégorie <strong>"{categoryName}"</strong>.
                Si aucune configuration n'est définie, la configuration globale sera utilisée.
              </p>
            </div>
          </div>
        </div>

        <Form
          ref={el => {
            // Stocker la référence sans causer de re-render
            if (el) {
              formsRef.current[categoryName] = el;
            }
          }}
          layout="vertical"
          initialValues={config ? {
            facebook_pixel_id: config.facebook_pixel_id,
            facebook_access_token: config.facebook_access_token,
            facebook_test_code: config.facebook_test_code,
            facebook_enabled: config.facebook_enabled
          } : {
            facebook_enabled: true
          }}
          onFinish={(values) => saveCategoryConfig(categoryName, values)}
          className="max-w-2xl"
        >
          <Form.Item
            name="facebook_enabled"
            label="Activer le pixel pour cette catégorie"
            valuePropName="checked"
            extra="Activez cette option pour utiliser un pixel spécifique pour cette catégorie"
          >
            <Switch
              onChange={(checked) => {
                if (config) {
                  toggleCategoryConfig(categoryName, checked);
                }
              }}
            />
          </Form.Item>

          <Divider />

          <Form.Item
            name="facebook_pixel_id"
            label="ID du Pixel Facebook"
            rules={[
              { required: true, message: 'Veuillez saisir l\'ID du pixel Facebook' },
              { pattern: /^\d+$/, message: 'L\'ID du pixel doit contenir uniquement des chiffres' }
            ]}
            extra="ID du pixel Facebook spécifique à cette catégorie"
          >
            <Input
              placeholder="Ex: 1234567890123456"
              prefix={<FacebookOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="facebook_access_token"
            label="Token d'accès Facebook"
            rules={[
              { required: true, message: 'Veuillez saisir le token d\'accès Facebook' }
            ]}
            extra="Token d'accès pour l'API Marketing Facebook"
          >
            <Input.Password
              placeholder="Saisissez votre token d'accès Facebook"
            />
          </Form.Item>

          <Form.Item
            name="facebook_test_code"
            label="Code de test Facebook (optionnel)"
            extra="Code de test pour valider les événements en mode développement"
          >
            <Input
              placeholder="Ex: TEST12345"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving[categoryName]}
                icon={<SaveOutlined />}
              >
                Sauvegarder
              </Button>

              {config && (
                <Tooltip title="Supprimer cette configuration et revenir à la configuration globale">
                  <Button
                    danger
                    loading={deleting[categoryName]}
                    icon={<DeleteOutlined />}
                    onClick={() => deleteCategoryConfig(categoryName)}
                  >
                    Supprimer la configuration
                  </Button>
                </Tooltip>
              )}

              <Button
                icon={<ReloadOutlined />}
                onClick={loadCategoryConfigs}
                loading={loading}
              >
                Recharger
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card
        title={
          <div className="flex items-center space-x-2">
            <FacebookOutlined className="text-blue-600" data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} />
            <span>Configuration des Pixels par Catégorie</span>
          </div>
        }
        className="shadow-sm"
        extra={
          <Space>
            <Tag color="blue" className="private-tag-accent">{Object.keys(categoryConfigs).length} catégories configurées</Tag>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCategoryConfigs}
              loading={loading}
              size="small"
            >
              Recharger
            </Button>
          </Space>
        }
      >
        <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-start space-x-2">
            <InfoCircleOutlined className="text-yellow-600 mt-1" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Comment ça fonctionne ?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Configurez un pixel Facebook différent pour chaque catégorie de produit</li>
                <li>Si aucune configuration spécifique n'est définie, la configuration globale sera utilisée</li>
                <li>Vous pouvez activer/désactiver chaque configuration individuellement</li>
                <li>Les événements de tracking utiliseront automatiquement le bon pixel selon la catégorie du produit</li>
              </ul>
            </div>
          </div>
        </div>

        {loading && availableCategories.length === 0 ? (
          <div className="text-center py-8">
            <Empty description="Chargement des catégories..." />
          </div>
        ) : (
          <Collapse
            accordion
            className="bg-white"
            bordered={false}
          >
            {availableCategories.map(categoryName => (
              <Panel
                header={getPanelHeader(categoryName)}
                key={categoryName}
                className="mb-2"
              >
                {getPanelContent(categoryName)}
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>

      {/* Résumé des configurations */}
      {Object.keys(categoryConfigs).length > 0 && (
        <Card
          title="Résumé des configurations"
          size="small"
          className="shadow-sm"
        >
          <Space direction="vertical" className="w-full">
            {Object.keys(categoryConfigs).map(categoryName => {
              const config = categoryConfigs[categoryName];
              return (
                <div key={categoryName} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <Space>
                    <span className="font-medium">{categoryName}</span>
                    <Tag color={config.facebook_enabled ? 'green' : 'orange'}>
                      {config.facebook_enabled ? 'Actif' : 'Inactif'}
                    </Tag>
                  </Space>
                  <span className="text-xs text-gray-500">
                    Pixel ID: {config.facebook_pixel_id}
                  </span>
                </div>
              );
            })}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default FacebookPixelCategories;
